import { useState, useEffect, useRef } from "react";
import {
  Plus,
  Edit2,
  Trash2,
  Search,
  Server,
  Upload,
  Download,
  AlertCircle,
  CheckCircle2,
  X,
  Wifi,
  Monitor,
  Cpu,
  HardDrive,
  Shield,
  Route,
  Network,
  Boxes,
  Container,
  CalendarDays,
  type LucideIcon,
} from "lucide-react";
import {
  ReactFlow,
  Background,
  Controls,
  Handle,
  Position,
  useNodesState,
  useEdgesState,
  MarkerType,
  type ReactFlowInstance,
  type Node,
  type Edge,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import {
  listCiDevices,
  createCiDevice,
  updateCiDevice,
  deleteCiDevice,
  importDevices,
  downloadDeviceImportTemplate,
  getDeviceRelationships,
  addDeviceRelationship,
  RelationshipType,
  DeviceResponse,
  DeviceType,
  DeviceStatus,
  DeviceImportResult,
  DeviceRelationshipGraphResponse,
  CiRelationshipResponse,
  listOrgUnits,
  OrgUnit,
} from "../../api/frameworksApi";
import { useToastStore } from "../../stores/toastStore";
import { RBACAction, RBACMultiAction } from "../components/RBACButton";
import {
  Modal,
  FormField,
  FormInput,
  FormSelect,
  ModalFooterButtons,
} from "../components/Modal";
import { DataTable, Column } from "../components/DataTable";
import { Tip } from "../components/ui/tooltip";
import { DeleteConfirmDialog } from "../components/DeleteConfirmDialog";
import {
  StatusBadge,
  flattenOrgHierarchy,
  DEVICE_TYPE_COLORS,
} from "./cmdb/cmdbUtils";

type CiStatus = DeviceStatus;

interface CiForm {
  orgId: string;
  name: string;
  type: DeviceType | "";
  status: CiStatus | "";
  description: string;
  // connectivity
  ipAddress: string;
  managementIp: string;
  virtualIp: string;
  hostname: string;
  // os
  osName: string;
  osVersion: string;
  // hardware
  cpuCores: string;
  ramGb: string;
  diskGb: string;
  // virtualisation
  hypervisor: string;
  parentCiId: string;
  // container
  imageName: string;
  namespace: string;
  registryUrl: string;
  // storage
  totalCapacityTb: string;
  usedCapacityTb: string;
}

const defaultForm: CiForm = {
  orgId: "",
  name: "",
  type: "",
  status: "",
  description: "",
  ipAddress: "",
  managementIp: "",
  virtualIp: "",
  hostname: "",
  osName: "",
  osVersion: "",
  cpuCores: "",
  ramGb: "",
  diskGb: "",
  hypervisor: "",
  parentCiId: "",
  imageName: "",
  namespace: "",
  registryUrl: "",
  totalCapacityTb: "",
  usedCapacityTb: "",
};

const HYPERVISOR_OPTIONS = [
  { value: "VMware ESXi", label: "VMware ESXi" },
  { value: "Hyper-V", label: "Hyper-V" },
  { value: "KVM", label: "KVM" },
  { value: "Xen", label: "Xen" },
  { value: "Proxmox", label: "Proxmox" },
];

// which fields each type requires/shows
const TYPE_FIELDS: Record<
  DeviceType,
  {
    ipAddress?: boolean;
    managementIp?: boolean;
    virtualIp?: boolean;
    hostname?: boolean;
    osName?: boolean;
    osVersion?: boolean;
    cpuCores?: boolean;
    ramGb?: boolean;
    diskGb?: boolean;
    hypervisor?: boolean;
    parentCiId?: "SERVER";
    imageName?: boolean;
    namespace?: boolean;
    registryUrl?: boolean;
    totalCapacityTb?: boolean;
    usedCapacityTb?: boolean;
  }
> = {
  SERVER: {
    ipAddress: true,
    hostname: true,
    osName: true,
    osVersion: true,
    cpuCores: true,
    ramGb: true,
    diskGb: true,
  },
  VM: {
    ipAddress: true,
    hostname: true,
    osName: true,
    osVersion: true,
    cpuCores: true,
    ramGb: true,
    diskGb: true,
    hypervisor: true,
    parentCiId: "SERVER",
  },
  FIREWALL: {
    ipAddress: true,
    managementIp: true,
    osName: true,
    osVersion: true,
  },
  SWITCH: { managementIp: true, osName: true, osVersion: true },
  ROUTER: {
    ipAddress: true,
    managementIp: true,
    osName: true,
    osVersion: true,
  },
  LB: { virtualIp: true, managementIp: true, osName: true, osVersion: true },
  CONTAINER: {
    imageName: true,
    namespace: true,
    parentCiId: "SERVER",
    registryUrl: true,
  },
  STORAGE: {
    osName: true,
    osVersion: true,
    totalCapacityTb: true,
    usedCapacityTb: true,
  },
};

// ─── Drawer constants ──────────────────────────────────────────────────────────

const DRAWER_TYPE_ICONS: Record<DeviceType, LucideIcon> = {
  SERVER: Server,
  VM: Boxes,
  FIREWALL: Shield,
  SWITCH: Network,
  ROUTER: Route,
  LB: Route,
  CONTAINER: Container,
  STORAGE: HardDrive,
};

const DRAWER_TYPE_HEX: Record<DeviceType, string> = {
  SERVER: "#60a5fa",
  VM: "#c084fc",
  FIREWALL: "#f87171",
  SWITCH: "#fbbf24",
  ROUTER: "#fb923c",
  LB: "#34d399",
  CONTAINER: "#22d3ee",
  STORAGE: "#94a3b8",
};

const DRAWER_STATUS_BADGE: Record<string, string> = {
  ACTIVE: "bg-emerald-500/15 text-emerald-400 border border-emerald-500/25",
  INACTIVE: "bg-gray-500/15 text-gray-400 border border-gray-500/25",
  MAINTENANCE: "bg-amber-500/15 text-amber-400 border border-amber-500/25",
  DECOMMISSIONED: "bg-red-500/15 text-red-400 border border-red-500/25",
  RUNNING: "bg-emerald-500/15 text-emerald-400 border border-emerald-500/25",
};

// ─── Topology helpers (shared with drawer) ───────────────────────────────────

const NODE_W = 200;
const NODE_GAP = 56;
const ROW_H = 260;

const TOPO_EDGE_COLORS: Record<string, string> = {
  "runs on": "#818cf8",
  "connected to": "#34d399",
  behind: "#fb923c",
  "attached to": "#fbbf24",
  "protected by": "#f87171",
};

const TOPO_TYPE_HEX: Record<string, string> = {
  SERVER: "#60a5fa",
  VM: "#c084fc",
  FIREWALL: "#f87171",
  SWITCH: "#fbbf24",
  ROUTER: "#fb923c",
  LB: "#34d399",
  CONTAINER: "#22d3ee",
  STORAGE: "#94a3b8",
};

const TOPO_TYPE_COLORS: Record<string, string> = {
  SERVER: "bg-blue-100 text-blue-700 dark:bg-blue-500/20 dark:text-blue-400",
  VM: "bg-purple-100 text-purple-700 dark:bg-purple-500/20 dark:text-purple-400",
  FIREWALL: "bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-400",
  SWITCH:
    "bg-yellow-100 text-yellow-700 dark:bg-yellow-500/20 dark:text-yellow-400",
  ROUTER:
    "bg-orange-100 text-orange-700 dark:bg-orange-500/20 dark:text-orange-400",
  LB: "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-400",
  CONTAINER: "bg-cyan-100 text-cyan-700 dark:bg-cyan-500/20 dark:text-cyan-400",
  STORAGE:
    "bg-slate-100 text-slate-700 dark:bg-slate-500/20 dark:text-slate-400",
};

const EDGE_LABEL_MAP: Record<string, string> = {
  HOSTED_ON: "runs on",
  CONNECTED_TO: "connected to",
  BEHIND: "behind",
  ATTACHED_TO: "attached to",
  PROTECTED_BY: "protected by",
};

// Format device type from ALL_CAPS to Title Case (e.g., SERVER -> Server, LB -> LB)
const formatDeviceType = (type: string): string => {
  if (type === "LB") return "LB";
  if (type === "VM") return "VM";
  return type.charAt(0).toUpperCase() + type.slice(1).toLowerCase();
};

function buildDrawerGraph(data: DeviceRelationshipGraphResponse): {
  nodes: Node[];
  edges: Edge[];
} {
  const nodesMap = new Map<string, Node>();
  const edgesArr: Edge[] = [];
  const seen = new Set<string>();

  function addNode(
    id: string,
    name: string,
    type: string,
    status: string,
    row: number,
    colIndex: number,
    colTotal: number,
    isRoot: boolean,
  ) {
    if (seen.has(id)) return;
    seen.add(id);
    const totalW = colTotal * (NODE_W + NODE_GAP) - NODE_GAP;
    const x = colIndex * (NODE_W + NODE_GAP) - totalW / 2;
    nodesMap.set(id, {
      id,
      type: "drawerDeviceNode",
      position: { x, y: row * ROW_H },
      sourcePosition: Position.Bottom,
      targetPosition: Position.Top,
      style: { width: NODE_W },
      data: { label: name, type, status, isRoot },
    });
  }

  const outgoing = [
    ...data.hostedOn.map((r) => ({
      ...r,
      edgeLabel: EDGE_LABEL_MAP.HOSTED_ON,
    })),
    ...data.connectedTo.map((r) => ({
      ...r,
      edgeLabel: EDGE_LABEL_MAP.CONNECTED_TO,
    })),
    ...data.behind.map((r) => ({ ...r, edgeLabel: EDGE_LABEL_MAP.BEHIND })),
    ...data.attachedTo.map((r) => ({
      ...r,
      edgeLabel: EDGE_LABEL_MAP.ATTACHED_TO,
    })),
    ...data.protectedBy.map((r) => ({
      ...r,
      edgeLabel: EDGE_LABEL_MAP.PROTECTED_BY,
    })),
  ];
  const incoming = [
    ...data.hosts.map((r) => ({ ...r, edgeLabel: EDGE_LABEL_MAP.HOSTED_ON })),
    ...data.connectedFrom.map((r) => ({
      ...r,
      edgeLabel: EDGE_LABEL_MAP.CONNECTED_TO,
    })),
    ...data.backends.map((r) => ({ ...r, edgeLabel: EDGE_LABEL_MAP.BEHIND })),
    ...data.attachedFrom.map((r) => ({
      ...r,
      edgeLabel: EDGE_LABEL_MAP.ATTACHED_TO,
    })),
    ...data.protects.map((r) => ({
      ...r,
      edgeLabel: EDGE_LABEL_MAP.PROTECTED_BY,
    })),
  ];

  const uniqueOut = Array.from(
    new Map(outgoing.map((r) => [r.peerCiId, r])).values(),
  );
  const uniqueIn = Array.from(
    new Map(incoming.map((r) => [r.peerCiId, r])).values(),
  );
  const maxCols = Math.max(uniqueOut.length, uniqueIn.length, 1);

  addNode(
    data.deviceId,
    data.deviceName,
    data.deviceType,
    data.deviceStatus,
    1,
    Math.floor(maxCols / 2),
    maxCols,
    true,
  );
  uniqueIn.forEach((r, i) =>
    addNode(
      r.peerCiId,
      r.peerName,
      r.peerType,
      r.peerStatus,
      0,
      i,
      uniqueIn.length,
      false,
    ),
  );
  uniqueOut.forEach((r, i) =>
    addNode(
      r.peerCiId,
      r.peerName,
      r.peerType,
      r.peerStatus,
      2,
      i,
      uniqueOut.length,
      false,
    ),
  );

  function makeEdge(
    id: string,
    source: string,
    target: string,
    label: string,
  ): Edge {
    const color = TOPO_EDGE_COLORS[label] ?? "#8b93ff";
    return {
      id,
      source,
      target,
      label,
      type: "bezier",
      animated: true,
      markerEnd: { type: MarkerType.ArrowClosed, color, width: 18, height: 18 },
      style: { stroke: color, strokeWidth: 2 },
      labelStyle: { fontSize: 10, fontWeight: 700, fill: color },
      labelBgStyle: { fill: "hsl(var(--card))", fillOpacity: 0.95 },
      labelBgPadding: [6, 3] as [number, number],
      labelBgBorderRadius: 6,
    };
  }

  outgoing.forEach((r) =>
    edgesArr.push(makeEdge(r.id, data.deviceId, r.peerCiId, r.edgeLabel)),
  );
  incoming.forEach((r) =>
    edgesArr.push(makeEdge(r.id, r.peerCiId, data.deviceId, r.edgeLabel)),
  );

  return { nodes: Array.from(nodesMap.values()), edges: edgesArr };
}

function DrawerDeviceNode({
  data,
}: {
  data: { label: string; type: string; status: string; isRoot: boolean };
}) {
  const accent = TOPO_TYPE_HEX[data.type] ?? "#8b93ff";
  const IconComp = DRAWER_TYPE_ICONS[data.type as DeviceType] ?? Server;
  return (
    <div
      style={{
        borderColor: data.isRoot ? "#818cf8" : accent,
        borderWidth: data.isRoot ? 2 : 1.5,
        boxShadow: data.isRoot
          ? `0 0 0 3px rgba(129,140,248,0.2), 0 4px 16px rgba(0,0,0,0.15)`
          : `0 2px 10px rgba(0,0,0,0.1)`,
      }}
      className="relative px-4 py-3 rounded-xl border min-w-[170px] max-w-[210px] bg-card"
    >
      <div
        className="absolute left-0 top-4 bottom-4 w-[3px] rounded-r-full"
        style={{ backgroundColor: accent }}
      />
      <Handle
        type="target"
        position={Position.Top}
        style={{
          background: accent,
          width: 9,
          height: 9,
          border: "2px solid hsl(var(--card))",
        }}
      />
      <div className="flex items-center gap-2 mb-2 pl-3">
        <div
          className="flex-shrink-0 size-6 rounded-md flex items-center justify-center"
          style={{ backgroundColor: `${accent}22` }}
        >
          <IconComp className="size-3.5" style={{ color: accent }} />
        </div>
        <span className="text-sm font-semibold text-foreground truncate">
          {data.label}
        </span>
      </div>
      <div className="flex gap-1.5 flex-wrap pl-3">
        <span
          className={`text-[9px] px-2 py-0.5 rounded-full font-bold ${TOPO_TYPE_COLORS[data.type] ?? ""}`}
        >
          {formatDeviceType(data.type)}
        </span>
        <span
          className={`text-[9px] px-2 py-0.5 rounded-full font-bold ${DRAWER_STATUS_BADGE[data.status] ?? "bg-muted d"}`}
        >
          {data.status}
        </span>
      </div>
      <Handle
        type="source"
        position={Position.Bottom}
        style={{
          background: accent,
          width: 9,
          height: 9,
          border: "2px solid hsl(var(--card))",
        }}
      />
    </div>
  );
}

const drawerNodeTypes = { drawerDeviceNode: DrawerDeviceNode };

// ─── Device Drill-Down Drawer ─────────────────────────────────────────────────

const REL_SECTION_LABELS: Record<
  string,
  { label: string; direction: "out" | "in" }
> = {
  hostedOn: { label: "Hosted On", direction: "out" },
  connectedTo: { label: "Connected To", direction: "out" },
  behind: { label: "Behind", direction: "out" },
  attachedTo: { label: "Attached To", direction: "out" },
  protectedBy: { label: "Protected By", direction: "out" },
  hosts: { label: "Hosts", direction: "in" },
  connectedFrom: { label: "Connected From", direction: "in" },
  backends: { label: "Backends", direction: "in" },
  attachedFrom: { label: "Attached From", direction: "in" },
  protects: { label: "Protects", direction: "in" },
};

function DeviceDrawer({
  device,
  onClose,
  onEdit,
  onDelete,
  activeTab,
  setActiveTab,
  onRelLoad,
}: {
  device: DeviceResponse;
  onClose: () => void;
  onEdit: (d: DeviceResponse) => void;
  onDelete: (d: DeviceResponse) => void;
  activeTab: "info" | "topology";
  setActiveTab: (tab: "info" | "topology") => void;
  onRelLoad: (hasRels: boolean) => void;
}) {
  const Icon = DRAWER_TYPE_ICONS[device.type] ?? Server;
  const accent = DRAWER_TYPE_HEX[device.type] ?? "#8b93ff";

  const [relGraph, setRelGraph] =
    useState<DeviceRelationshipGraphResponse | null>(null);
  const [relLoading, setRelLoading] = useState(true);
  const [nodes, setNodes, onNodesChange] = useNodesState<Node>([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>([]);
  const [flowInstance, setFlowInstance] = useState<ReactFlowInstance | null>(
    null,
  );

  // Add relationship modal state
  const [showAddRel, setShowAddRel] = useState(false);
  const [allDevices, setAllDevices] = useState<DeviceResponse[]>([]);
  const [relType, setRelType] = useState<RelationshipType | "">("");
  const [relTargetId, setRelTargetId] = useState("");
  const [relNotes, setRelNotes] = useState("");
  const [relSaving, setRelSaving] = useState(false);
  const { showSuccess, showError } = useToastStore();

  const REL_TYPES: { value: RelationshipType; label: string }[] = [
    { value: "CONNECTED_TO", label: "Connected To" },
    { value: "HOSTED_ON", label: "Hosted On" },
    { value: "BEHIND", label: "Behind" },
    { value: "ATTACHED_TO", label: "Attached To" },
    { value: "PROTECTED_BY", label: "Protected By" },
  ];

  const fetchRelationships = () => {
    setRelLoading(true);
    getDeviceRelationships(device.id)
      .then((r) => {
        setRelGraph(r.data);
        const { nodes: n, edges: e } = buildDrawerGraph(r.data);
        setNodes(n);
        setEdges(e);
        const totalRels =
          r.data.hostedOn.length +
          r.data.connectedTo.length +
          r.data.behind.length +
          r.data.attachedTo.length +
          r.data.protectedBy.length +
          r.data.hosts.length +
          r.data.connectedFrom.length +
          r.data.backends.length +
          r.data.attachedFrom.length +
          r.data.protects.length;
        onRelLoad(totalRels > 0);
      })
      .catch(() => {
        onRelLoad(false);
      })
      .finally(() => setRelLoading(false));
  };

  useEffect(() => {
    if (activeTab !== "topology" || !flowInstance || nodes.length === 0) return;
    const t = window.setTimeout(
      () => flowInstance.fitView({ padding: 0.2, duration: 350 }),
      80,
    );
    return () => window.clearTimeout(t);
  }, [activeTab, flowInstance, nodes]);

  useEffect(() => {
    fetchRelationships();
  }, [device.id]);

  const openAddRel = () => {
    setRelTargetId("");
    setRelType("");
    setRelNotes("");
    if (allDevices.length === 0) {
      listCiDevices()
        .then((r) =>
          setAllDevices(
            (r.data ?? []).filter((d: DeviceResponse) => d.id !== device.id),
          ),
        )
        .catch(() => {});
    }
    setShowAddRel(true);
  };

  const handleAddRelationship = async () => {
    if (!relTargetId || !relType) return;
    setRelSaving(true);
    try {
      await addDeviceRelationship(device.id, {
        targetCiId: relTargetId,
        relationshipType: relType,
        notes: relNotes.trim() || undefined,
      });
      showSuccess("Success", "Relationship added successfully.");
      setShowAddRel(false);
      fetchRelationships();
    } catch {
      showError("Error", "Failed to add relationship.");
    } finally {
      setRelSaving(false);
    }
  };

  type Field = { label: string; value: string | number | null | undefined };
  const connectivity: Field[] = [
    { label: "IP Address", value: device.ipAddress },
    { label: "Management IP", value: device.managementIp },
    { label: "Virtual IP", value: device.virtualIp },
    { label: "Hostname", value: device.hostname },
  ].filter((f) => f.value);

  const os: Field[] = [
    { label: "OS Name", value: device.osName },
    { label: "OS Version", value: device.osVersion },
  ].filter((f) => f.value);

  const hardware: Field[] = [
    {
      label: "CPU Cores",
      value: device.cpuCores != null ? `${device.cpuCores} cores` : null,
    },
    { label: "RAM", value: device.ramGb != null ? `${device.ramGb} GB` : null },
    {
      label: "Disk",
      value: device.diskGb != null ? `${device.diskGb} GB` : null,
    },
  ].filter((f) => f.value);

  const container: Field[] = [
    { label: "Image", value: device.imageName },
    { label: "Namespace", value: device.namespace },
    { label: "Registry URL", value: device.registryUrl },
  ].filter((f) => f.value);

  const storage: Field[] = [
    {
      label: "Total Capacity",
      value:
        device.totalCapacityTb != null ? `${device.totalCapacityTb} TB` : null,
    },
    {
      label: "Used Capacity",
      value:
        device.usedCapacityTb != null ? `${device.usedCapacityTb} TB` : null,
    },
  ].filter((f) => f.value);

  const infoSections = [
    {
      title: "Connectivity",
      icon: Wifi,
      color: "text-indigo-600 dark:text-indigo-400",
      bg: "bg-indigo-500/10",
      fields: connectivity,
    },
    {
      title: "Operating System",
      icon: Monitor,
      color: "text-violet-600 dark:text-violet-400",
      bg: "bg-violet-500/10",
      fields: os,
    },
    {
      title: "Hardware",
      icon: Cpu,
      color: "text-amber-600 dark:text-amber-400",
      bg: "bg-amber-500/10",
      fields: hardware,
    },
    {
      title: "Container",
      icon: Container,
      color: "text-cyan-600 dark:text-cyan-400",
      bg: "bg-cyan-500/10",
      fields: container,
    },
    {
      title: "Storage",
      icon: HardDrive,
      color: "text-slate-500 dark:text-slate-300",
      bg: "bg-slate-500/10",
      fields: storage,
    },
  ].filter((s) => s.fields.length > 0);

  const relSections = relGraph
    ? Object.entries(REL_SECTION_LABELS)
        .map(([key, meta]) => ({
          key,
          label: meta.label,
          direction: meta.direction,
          items:
            (relGraph as unknown as Record<string, CiRelationshipResponse[]>)[
              key
            ] ?? [],
        }))
        .filter((s) => s.items.length > 0)
    : [];

  return (
    <div className="bg-card flex flex-col h-full">
      {/* Header — hidden in topology view */}
      {activeTab !== "topology" && (
        <div className="flex items-start gap-3 px-6 py-4 border-b border-border flex-shrink-0">
          <div
            className="flex-shrink-0 size-11 rounded-xl flex items-center justify-center mt-0.5"
            style={{
              backgroundColor: `${accent}20`,
              border: `1px solid ${accent}30`,
            }}
          >
            <Icon className="size-5" style={{ color: accent }} />
          </div>
          <div className="min-w-0 flex-1">
            <h2 className="text-base font-bold text-foreground truncate">
              {device.name}
            </h2>
            <div className="flex items-center gap-2 mt-1">
              <span
                className={`text-[10px] font-bold px-2 py-0.5 rounded-md ${DEVICE_TYPE_COLORS[device.type] ?? ""}`}
              >
                {formatDeviceType(device.type)}
              </span>
              <span
                className={`text-[10px] font-semibold px-2 py-0.5 rounded-md ${DRAWER_STATUS_BADGE[device.status] ?? "bg-gray-500/15 text-gray-400"}`}
              >
                {device.status}
              </span>
            </div>
            {device.description && (
              <p className="text-xs d mt-2 leading-relaxed">
                {device.description}
              </p>
            )}
          </div>
          <div className="flex flex-col items-end justify-between flex-shrink-0 self-stretch">
            <button
              onClick={onClose}
              className="p-1.5 rounded-lg hover:bg-muted/60 d hover:text-foreground transition-colors"
            >
              <X className="size-4" />
            </button>
            <div className="flex items-center gap-1.5">
              <RBACAction module="cmdb" action="update">
                <button
                  onClick={() => onEdit(device)}
                  className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-indigo-500/40 bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 hover:bg-indigo-500/20 text-xs font-semibold transition-colors"
                >
                  <Edit2 className="size-3.5" /> Edit
                </button>
              </RBACAction>
              <RBACAction module="cmdb" action="delete">
                <button
                  onClick={() => onDelete(device)}
                  className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-red-500/40 bg-red-500/10 text-red-600 dark:text-red-400 hover:bg-red-500/20 text-xs font-semibold transition-colors"
                >
                  <Trash2 className="size-3.5" /> Delete
                </button>
              </RBACAction>
            </div>
          </div>
        </div>
      )}

      {/* Topology view — inside panel */}
      {activeTab === "topology" && (
        <div className="flex flex-col bg-[#080d14] flex-1 min-h-0">
          {/* Legend bar */}
          <div className="flex items-center justify-between px-4 py-2 bg-[#0d1117]/90 border-b border-[#1e293b] flex-shrink-0">
            <div className="flex items-center gap-3">
              <p className="text-xs text-slate-400">
                <span className="font-semibold text-white">{nodes.length}</span>{" "}
                nodes ·{" "}
                <span className="font-semibold text-white">{edges.length}</span>{" "}
                edges
              </p>
              <div className="hidden sm:flex items-center gap-2 flex-wrap">
                {Object.entries(TOPO_EDGE_COLORS).map(([label, color]) => (
                  <span
                    key={label}
                    className="flex items-center gap-1 text-[10px] text-slate-400"
                  >
                    <span
                      className="inline-block w-3 h-0.5 rounded-full"
                      style={{ backgroundColor: color }}
                    />
                    {label}
                  </span>
                ))}
              </div>
            </div>
            <button
              type="button"
              onClick={() =>
                flowInstance?.fitView({
                  padding: 0.35,
                  includeHiddenNodes: true,
                  duration: 400,
                })
              }
              className="text-[11px] px-2.5 py-1 rounded-lg border border-white/20 text-slate-300 hover:bg-white/10 hover:text-white transition-colors flex-shrink-0"
            >
              Fit Graph
            </button>
          </div>
          {/* Canvas */}
          <div className="flex-1 relative">
            {relLoading ? (
              <div className="absolute inset-0 flex items-center justify-center text-slate-400 text-sm">
                Loading topology…
              </div>
            ) : nodes.length <= 1 ? (
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-slate-400">
                <Network className="size-8 opacity-30" />
                <p className="text-sm font-medium">
                  No relationships to visualise
                </p>
                <p className="text-xs opacity-60">
                  Add relationships to see the topology graph.
                </p>
              </div>
            ) : (
              <ReactFlow
                className="cmdb-topology-flow"
                nodes={nodes}
                edges={edges}
                onNodesChange={onNodesChange}
                onEdgesChange={onEdgesChange}
                nodeTypes={drawerNodeTypes}
                onInit={setFlowInstance}
                fitView
                fitViewOptions={{ padding: 0.35, includeHiddenNodes: true }}
                minZoom={0.15}
                maxZoom={2}
                proOptions={{ hideAttribution: true }}
              >
                <Background color="#1e2d4a" gap={30} size={1.5} />
                <Controls
                  style={{
                    background: "rgba(15,23,42,0.85)",
                    border: "1px solid rgba(255,255,255,0.1)",
                    borderRadius: 8,
                  }}
                />
              </ReactFlow>
            )}
          </div>
          <style>{`
              .cmdb-topology-flow .react-flow__controls { box-shadow: 0 6px 20px rgba(0,0,0,0.35); border: 1px solid rgba(99,102,241,0.4); border-radius: 10px; overflow: hidden; }
              .cmdb-topology-flow .react-flow__controls-button { background: #0f172a; border-bottom: 1px solid rgba(148,163,184,0.25); color: #e2e8f0; width: 30px; height: 30px; }
              .cmdb-topology-flow .react-flow__controls-button:hover { background: #1e293b; }
              .cmdb-topology-flow .react-flow__controls-button svg { fill: #e2e8f0; stroke: #e2e8f0; }
            `}</style>
        </div>
      )}

      {/* Scrollable body — all details */}
      {activeTab === "info" && (
        <div className="overflow-y-auto flex-1">
          {/* Created / Updated tiles */}
          <div className="grid grid-cols-2 gap-3 p-5">
            <div
              className="flex items-center gap-3 rounded-xl bg-muted/30 border border-border border-b-2 px-4 py-3"
              style={{
                borderBottomColor: accent,
                boxShadow: `0 4px 12px -4px ${accent}40`,
              }}
            >
              <CalendarDays
                className="size-4 flex-shrink-0"
                style={{ color: accent }}
              />
              <div className="min-w-0">
                <p className="text-[10px] tracking-wider d font-medium">
                  Created
                </p>
                <p className="text-sm font-semibold text-foreground">
                  {new Date(device.createdAt).toLocaleDateString("en-GB", {
                    day: "2-digit",
                    month: "short",
                    year: "numeric",
                  })}
                </p>
              </div>
            </div>
            <div
              className="flex items-center gap-3 rounded-xl bg-muted/30 border border-border border-b-2 border-b-violet-500 px-4 py-3"
              style={{ boxShadow: "0 4px 12px -4px rgba(139,92,246,0.35)" }}
            >
              <CalendarDays className="size-4 text-violet-400 flex-shrink-0" />
              <div className="min-w-0">
                <p className="text-[10px] tracking-wider d font-medium">
                  Updated
                </p>
                <p className="text-sm font-semibold text-foreground">
                  {new Date(device.updatedAt).toLocaleDateString("en-GB", {
                    day: "2-digit",
                    month: "short",
                    year: "numeric",
                  })}
                </p>
              </div>
            </div>
          </div>

          {/* Device info sections */}
          {infoSections.length > 0 && (
            <div className="px-5 pb-5 space-y-3">
              {infoSections.map(
                ({ title, icon: SectionIcon, color, bg, fields }) => (
                  <div
                    key={title}
                    className="rounded-xl border border-border overflow-hidden"
                  >
                    <div
                      className={`flex items-center gap-2 px-4 py-2.5 border-b border-border ${bg}`}
                    >
                      <SectionIcon className={`size-3.5 ${color}`} />
                      <span
                        className={`text-[10px] font-bold tracking-wider ${color}`}
                      >
                        {title}
                      </span>
                    </div>
                    {fields.map((f) => (
                      <div
                        key={f.label}
                        className="flex items-start justify-between gap-4 px-4 py-2.5 border-b border-border/50 last:border-0 bg-muted/10"
                      >
                        <span className="text-xs text-foreground font-medium shrink-0">
                          {f.label}
                        </span>
                        <span className="text-sm font-semibold text-foreground font-mono min-w-0 text-right break-all">
                          {String(f.value)}
                        </span>
                      </div>
                    ))}
                  </div>
                ),
              )}
            </div>
          )}

          {/* Relationships */}
          <div className="px-5 pb-6">
            <div className="flex items-center justify-between gap-2 mb-3">
              <div className="flex items-center gap-2">
                <Network className="size-3.5 text-violet-600 dark:text-violet-400" />
                <span className="text-[10px] font-bold tracking-wider text-violet-600 dark:text-violet-400">
                  Relationships
                </span>
              </div>
              <button
                onClick={openAddRel}
                className="flex items-center gap-1 px-2.5 py-1 rounded-lg border border-violet-200 bg-violet-50 text-violet-600 dark:border-violet-500/25 dark:bg-violet-500/10 dark:text-violet-400 hover:bg-violet-100 dark:hover:bg-violet-500/20 transition-colors text-[11px] font-semibold"
              >
                <Plus className="size-3" />
                Add
              </button>
            </div>

            {relLoading && (
              <div className="text-sm d py-4 text-center">
                Loading relationships…
              </div>
            )}

            {!relLoading && relSections.length === 0 && (
              <div className="rounded-xl border border-border bg-muted/10 px-4 py-6 text-center text-sm d">
                No relationships found
              </div>
            )}

            {/* Add Relationship Modal */}
            <Modal
              open={showAddRel}
              onClose={() => !relSaving && setShowAddRel(false)}
              title="Add Relationship"
              description={`Link "${device.name}" to another device.`}
              size="sm"
              closable={!relSaving}
              footer={
                <ModalFooterButtons
                  onCancel={() => setShowAddRel(false)}
                  onSubmit={handleAddRelationship}
                  submitLabel={relSaving ? "Adding…" : "Add Relationship"}
                  submitDisabled={relSaving || !relTargetId || !relType}
                />
              }
            >
              <div className="space-y-4">
                <FormField label="Target Device" required>
                  <FormSelect
                    value={relTargetId}
                    onChange={setRelTargetId}
                    options={[
                      { value: "", label: "Select a device" },
                      ...allDevices.map((d) => ({
                        value: d.id,
                        label: `${d.name} (${d.type})`,
                      })),
                    ]}
                  />
                </FormField>
                <FormField label="Relationship Type" required>
                  <FormSelect
                    value={relType}
                    onChange={(v) => setRelType(v as RelationshipType)}
                    options={[
                      { value: "", label: "Select relationship type" },
                      ...REL_TYPES.map((r) => ({
                        value: r.value,
                        label: r.label,
                      })),
                    ]}
                  />
                </FormField>
                <FormField label="Notes">
                  <FormInput
                    value={relNotes}
                    onChange={setRelNotes}
                    placeholder="Optional notes about this relationship"
                    name="rel-notes"
                    autoComplete="off"
                  />
                </FormField>
              </div>
            </Modal>

            {!relLoading && relSections.length > 0 && (
              <div className="space-y-3">
                {relSections.map(({ key, label, direction, items }) => {
                  const isOut = direction === "out";
                  const dirColor = isOut
                    ? "bg-indigo-500/15 text-indigo-600 dark:text-indigo-300 border border-indigo-500/30"
                    : "bg-violet-500/15 text-violet-600 dark:text-violet-300 border border-violet-500/30";
                  const headerBg = isOut
                    ? "bg-indigo-500/10"
                    : "bg-violet-500/10";
                  const headerText = isOut
                    ? "text-indigo-600 dark:text-indigo-300"
                    : "text-violet-600 dark:text-violet-300";
                  return (
                    <div
                      key={key}
                      className="rounded-xl border border-border overflow-hidden"
                    >
                      <div
                        className={`flex items-center gap-2 px-4 py-2.5 border-b border-border ${headerBg}`}
                      >
                        <span
                          className={`text-[10px] font-bold tracking-wider ${headerText}`}
                        >
                          {label}
                        </span>
                        <span
                          className={`ml-auto text-[9px] font-bold px-2 py-0.5 rounded-full ${dirColor}`}
                        >
                          {isOut ? "OUTGOING" : "INCOMING"}
                        </span>
                      </div>
                      {items.map((rel) => {
                        const PeerIcon =
                          DRAWER_TYPE_ICONS[rel.peerType] ?? Server;
                        const peerAccent =
                          DRAWER_TYPE_HEX[rel.peerType] ?? "#8b93ff";
                        return (
                          <div
                            key={rel.id}
                            className="flex items-center gap-3 px-4 py-3 border-b border-border/50 last:border-0 bg-muted/10"
                          >
                            <div
                              className="flex-shrink-0 size-8 rounded-lg flex items-center justify-center"
                              style={{
                                backgroundColor: `${peerAccent}25`,
                                border: `1px solid ${peerAccent}30`,
                              }}
                            >
                              <PeerIcon
                                className="size-4"
                                style={{ color: peerAccent }}
                              />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-semibold text-foreground truncate">
                                {rel.peerName}
                              </p>
                              {rel.notes && (
                                <p className="text-[11px] d truncate mt-0.5">
                                  {rel.notes}
                                </p>
                              )}
                            </div>
                            <div className="flex items-center gap-1.5 flex-shrink-0">
                              <span
                                className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${DEVICE_TYPE_COLORS[rel.peerType] ?? ""}`}
                              >
                                {rel.peerType}
                              </span>
                              <span
                                className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${DRAWER_STATUS_BADGE[rel.peerStatus] ?? "bg-gray-500/15 text-gray-400"}`}
                              >
                                {rel.peerStatus}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function ImportErrorTable({
  errors,
  onDownload,
}: {
  errors: { row: number; field?: string; message: string }[];
  onDownload: () => void;
}) {
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <p className="text-sm font-medium">Errors ({errors.length})</p>
        <button
          onClick={onDownload}
          className="flex items-center gap-1.5 text-xs px-3 py-1.5 border border-border rounded hover:bg-muted transition-colors"
        >
          <Download className="size-3" />
          Download Error Report
        </button>
      </div>
      <div className="max-h-48 overflow-y-auto border border-border rounded-lg">
        <table className="w-full text-xs">
          <thead className="bg-muted sticky top-0">
            <tr>
              <th className="px-3 py-2 text-left font-medium w-16">Row</th>
              <th className="px-3 py-2 text-left font-medium w-24">Field</th>
              <th className="px-3 py-2 text-left font-medium">Message</th>
            </tr>
          </thead>
          <tbody>
            {errors.map((e, i) => (
              <tr key={i} className="border-t border-border hover:bg-muted/50">
                <td className="px-3 py-2 d">{e.row}</td>
                <td className="px-3 py-2 d">{e.field ?? "—"}</td>
                <td className="px-3 py-2 text-red-600 dark:text-red-400">
                  {e.message}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function CMDBConfigItems() {
  const [cis, setCis] = useState<DeviceResponse[]>([]);
  const [allDevices, setAllDevices] = useState<DeviceResponse[]>([]);
  const [orgOptions, setOrgOptions] = useState<OrgUnit[]>([]);
  const [loading, setLoading] = useState(false);
  const [drawerDevice, setDrawerDevice] = useState<DeviceResponse | null>(null);
  const [drawerActiveTab, setDrawerActiveTab] = useState<"info" | "topology">(
    "info",
  );
  const [drawerHasRelationships, setDrawerHasRelationships] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(0);
  const pageSize = 10;

  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<DeviceResponse | null>(null);
  const [form, setForm] = useState<CiForm>(defaultForm);
  const [saving, setSaving] = useState(false);

  const [deletingCi, setDeletingCi] = useState<DeviceResponse | null>(null);
  const [deleting, setDeleting] = useState(false);

  const [templateDownloading, setTemplateDownloading] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importOrgId, setImportOrgId] = useState("");
  const [importLoading, setImportLoading] = useState(false);
  const [importResult, setImportResult] = useState<DeviceImportResult | null>(
    null,
  );
  const [importSizeWarned, setImportSizeWarned] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    loadCis();
    loadOrgOptions();
  }, []);
  useEffect(() => {
    setPage(0);
  }, [searchQuery]);

  const parseNumberField = (
    value: string,
    label: string,
    options: { integer?: boolean; min?: number } = {},
  ) => {
    if (!value) return undefined;
    const parsed = Number(value);
    const min = options.min ?? 0;
    if (
      !Number.isFinite(parsed) ||
      parsed < min ||
      (options.integer && !Number.isInteger(parsed))
    ) {
      useToastStore
        .getState()
        .showError(
          "Error",
          `${label} must be ${options.integer ? "a whole number" : "a number"} greater than or equal to ${min}`,
        );
      return null;
    }
    return parsed;
  };

  const loadCis = async () => {
    setLoading(true);
    try {
      const res = await listCiDevices();
      const data = res.data || [];
      setCis(data);
      setAllDevices(data);
      setDrawerDevice((prev) => prev);
    } catch (err) {
      useToastStore
        .getState()
        .showError(
          "Error",
          err instanceof Error ? err.message : "Failed to load devices",
        );
    } finally {
      setLoading(false);
    }
  };

  const loadOrgOptions = async () => {
    try {
      const res = await listOrgUnits();
      setOrgOptions(flattenOrgHierarchy(res.data || [], ["ORGANIZATION"]));
    } catch {}
  };

  const openCreate = () => {
    setEditing(null);
    setForm(defaultForm);
    setShowModal(true);
  };

  const openEdit = (ci: DeviceResponse) => {
    setEditing(ci);
    setForm({
      orgId: ci.orgId,
      name: ci.name,
      type: ci.type,
      status: ci.status,
      description: ci.description || "",
      ipAddress: ci.ipAddress || "",
      managementIp: ci.managementIp || "",
      virtualIp: ci.virtualIp || "",
      hostname: ci.hostname || "",
      osName: ci.osName || "",
      osVersion: ci.osVersion || "",
      cpuCores: ci.cpuCores != null ? String(ci.cpuCores) : "",
      ramGb: ci.ramGb != null ? String(ci.ramGb) : "",
      diskGb: ci.diskGb != null ? String(ci.diskGb) : "",
      hypervisor: ci.hypervisor || "",
      parentCiId: ci.parentCiId || "",
      imageName: ci.imageName || "",
      namespace: ci.namespace || "",
      registryUrl: ci.registryUrl || "",
      totalCapacityTb:
        ci.totalCapacityTb != null ? String(ci.totalCapacityTb) : "",
      usedCapacityTb:
        ci.usedCapacityTb != null ? String(ci.usedCapacityTb) : "",
    });
    setShowModal(true);
  };

  const f = TYPE_FIELDS[form.type as DeviceType] ?? {};

  // Mirrors the required (asterisked) fields for the selected type so the Save
  // button stays disabled until every mandatory field is filled.
  const isFormValid =
    !!form.orgId &&
    !!form.name.trim() &&
    !!form.type &&
    !!form.status &&
    (!f.ipAddress || !!form.ipAddress.trim()) &&
    (!f.managementIp || !!form.managementIp.trim()) &&
    (!f.virtualIp || !!form.virtualIp.trim()) &&
    (!f.hostname || !!form.hostname.trim()) &&
    (!f.osName || !!form.osName.trim()) &&
    (!f.osVersion || !!form.osVersion.trim()) &&
    (!f.hypervisor || !!form.hypervisor) &&
    (!f.parentCiId || !!form.parentCiId) &&
    (!f.imageName || !!form.imageName.trim()) &&
    (!f.namespace || !!form.namespace.trim()) &&
    (!f.totalCapacityTb || !!form.totalCapacityTb.trim());

  const handleSubmit = async () => {
    if (!form.orgId) {
      useToastStore.getState().showError("Error", "Org unit is required");
      return;
    }
    if (!form.name.trim()) {
      useToastStore.getState().showError("Error", "Name is required");
      return;
    }
    if (!form.type) {
      useToastStore.getState().showError("Error", "Type is required");
      return;
    }
    if (!form.status) {
      useToastStore.getState().showError("Error", "Status is required");
      return;
    }
    if (f.ipAddress && !form.ipAddress.trim()) {
      useToastStore.getState().showError("Error", "IP Address is required");
      return;
    }
    if (f.managementIp && !form.managementIp.trim()) {
      useToastStore.getState().showError("Error", "Management IP is required");
      return;
    }
    if (f.virtualIp && !form.virtualIp.trim()) {
      useToastStore.getState().showError("Error", "Virtual IP is required");
      return;
    }
    if (f.hostname && !form.hostname.trim()) {
      useToastStore.getState().showError("Error", "Hostname is required");
      return;
    }
    if (f.osName && !form.osName.trim()) {
      useToastStore.getState().showError("Error", "OS Name is required");
      return;
    }
    if (f.hypervisor && !form.hypervisor) {
      useToastStore.getState().showError("Error", "Hypervisor is required");
      return;
    }
    if (f.parentCiId && !form.parentCiId) {
      useToastStore.getState().showError("Error", "Parent server is required");
      return;
    }
    if (f.imageName && !form.imageName.trim()) {
      useToastStore.getState().showError("Error", "Image name is required");
      return;
    }
    if (f.namespace && !form.namespace.trim()) {
      useToastStore.getState().showError("Error", "Namespace is required");
      return;
    }
    if (f.totalCapacityTb && !form.totalCapacityTb) {
      useToastStore.getState().showError("Error", "Total capacity is required");
      return;
    }

    const cpuCores = f.cpuCores
      ? parseNumberField(form.cpuCores, "CPU cores", { integer: true, min: 1 })
      : undefined;
    if (cpuCores === null) return;
    const ramGb = f.ramGb
      ? parseNumberField(form.ramGb, "RAM", { min: 0 })
      : undefined;
    if (ramGb === null) return;
    const diskGb = f.diskGb
      ? parseNumberField(form.diskGb, "Disk", { min: 0 })
      : undefined;
    if (diskGb === null) return;
    const totalCapacityTb = f.totalCapacityTb
      ? parseNumberField(form.totalCapacityTb, "Total capacity", { min: 0 })
      : undefined;
    if (totalCapacityTb === null) return;
    const usedCapacityTb = f.usedCapacityTb
      ? parseNumberField(form.usedCapacityTb, "Used capacity", { min: 0 })
      : undefined;
    if (usedCapacityTb === null) return;

    const payload: any = {
      orgId: form.orgId,
      name: form.name,
      type: form.type,
      status: form.status,
      description: form.description || undefined,
    };
    if (f.ipAddress) payload.ipAddress = form.ipAddress || undefined;
    if (f.managementIp) payload.managementIp = form.managementIp || undefined;
    if (f.virtualIp) payload.virtualIp = form.virtualIp || undefined;
    if (f.hostname) payload.hostname = form.hostname || undefined;
    if (f.osName) payload.osName = form.osName || undefined;
    if (f.osVersion) payload.osVersion = form.osVersion || undefined;
    if (f.cpuCores) payload.cpuCores = cpuCores;
    if (f.ramGb) payload.ramGb = ramGb;
    if (f.diskGb) payload.diskGb = diskGb;
    if (f.hypervisor) payload.hypervisor = form.hypervisor || undefined;
    if (f.parentCiId) payload.parentCiId = form.parentCiId || undefined;
    if (f.imageName) payload.imageName = form.imageName || undefined;
    if (f.namespace) payload.namespace = form.namespace || undefined;
    if (f.registryUrl) payload.registryUrl = form.registryUrl || undefined;
    if (f.totalCapacityTb) payload.totalCapacityTb = totalCapacityTb;
    if (f.usedCapacityTb) payload.usedCapacityTb = usedCapacityTb;

    setSaving(true);
    try {
      if (editing) {
        await updateCiDevice(editing.id, payload);
        useToastStore.getState().showSuccess("Success", "Device updated");
      } else {
        await createCiDevice(payload);
        useToastStore.getState().showSuccess("Success", "Device created");
      }
      setShowModal(false);
      loadCis();
    } catch (err) {
      useToastStore
        .getState()
        .showError(
          "Error",
          err instanceof Error ? err.message : "Operation failed",
        );
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deletingCi) return;
    setDeleting(true);
    try {
      await deleteCiDevice(deletingCi.id);
      useToastStore.getState().showSuccess("Success", "Device deleted");
      // Close the detail panel if it's showing the device we just deleted.
      if (drawerDevice?.id === deletingCi.id) {
        setDrawerDevice(null);
        setDrawerActiveTab("info");
        setDrawerHasRelationships(false);
      }
      setDeletingCi(null);
      loadCis();
    } catch (err) {
      useToastStore
        .getState()
        .showError(
          "Error",
          err instanceof Error ? err.message : "Delete failed",
        );
    } finally {
      setDeleting(false);
    }
  };

  const handleDownloadTemplate = async () => {
    setTemplateDownloading(true);
    try {
      await downloadDeviceImportTemplate();
    } catch (err) {
      useToastStore
        .getState()
        .showError(
          "Error",
          err instanceof Error ? err.message : "Failed to download template",
        );
    } finally {
      setTemplateDownloading(false);
    }
  };

  const openImportModal = () => {
    setImportFile(null);
    setImportOrgId("");
    setImportResult(null);
    setImportSizeWarned(false);
    setShowImportModal(true);
  };

  const handleFileChange = (file: File | null) => {
    setImportResult(null);
    if (!file) {
      setImportFile(null);
      return;
    }
    if (!file.name.toLowerCase().endsWith(".csv")) {
      useToastStore
        .getState()
        .showError("Error", "Only .csv files are accepted");
      setImportFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }
    const warned = file.size > 5 * 1024 * 1024;
    setImportSizeWarned(warned);
    if (warned)
      useToastStore
        .getState()
        .showError("Warning", "File is over 5 MB — import may be slow");
    setImportFile(file);
  };

  const handleImportUpload = async () => {
    if (!importOrgId) {
      useToastStore
        .getState()
        .showError("Error", "Please select an org unit before importing");
      return;
    }
    if (!importFile) return;
    setImportLoading(true);
    try {
      const res = await importDevices(importFile, importOrgId);
      const raw: any = res.data ?? res;
      // Normalize — backend may use different field names
      const normalized: DeviceImportResult = {
        total: raw.total ?? raw.totalCount ?? raw.totalRows ?? 0,
        success:
          raw.success ??
          raw.successCount ??
          raw.imported ??
          raw.successRows ??
          0,
        failed:
          raw.failed ??
          raw.failedCount ??
          raw.failedRows ??
          raw.errors?.length ??
          0,
        errors: raw.errors ?? raw.errorDetails ?? [],
      };
      setImportResult(normalized);
      if (normalized.failed === 0) {
        useToastStore
          .getState()
          .showSuccess(
            "Success",
            `All ${normalized.success} device${normalized.success !== 1 ? "s" : ""} imported successfully`,
          );
        loadCis();
      } else if (normalized.success > 0) {
        useToastStore
          .getState()
          .showError(
            "Warning",
            `${normalized.success} imported, ${normalized.failed} failed`,
          );
        loadCis();
      } else {
        useToastStore
          .getState()
          .showError(
            "Error",
            `Import failed — ${normalized.failed} row${normalized.failed !== 1 ? "s" : ""} had errors`,
          );
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Import failed";
      const is404 =
        msg.includes("404") || msg.toLowerCase().includes("not found");
      useToastStore
        .getState()
        .showError(
          "Error",
          is404
            ? "Selected org unit was not found. Please refresh and try again."
            : msg,
        );
    } finally {
      setImportLoading(false);
    }
  };

  const handleDownloadErrorReport = () => {
    if (!importResult?.errors?.length) return;
    const header = "Row,Field,Message";
    const rows = importResult.errors.map(
      (e) => `${e.row},"${e.field ?? ""}","${e.message.replace(/"/g, '""')}"`,
    );
    const csv = [header, ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "device-import-errors.csv";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const serverOptions = allDevices
    .filter((d) => d.type === "SERVER")
    .map((d) => ({
      value: d.id,
      label: `${d.name} (${d.ipAddress ?? d.hostname ?? d.id})`,
    }));

  const filtered = cis.filter(
    (ci) =>
      (ci.name || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
      (ci.ipAddress || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
      (ci.hostname || "").toLowerCase().includes(searchQuery.toLowerCase()),
  );

  const paginated = filtered.slice(page * pageSize, (page + 1) * pageSize);

  const columns: Column<DeviceResponse>[] = [
    {
      key: "_sno",
      header: "#",
      width: "w-10",
      align: "center",
      render: (_, i) => (
        <span className="d text-sm">{page * pageSize + i + 1}</span>
      ),
    },
    {
      key: "name",
      header: "Name",
      align: "left",
      render: (row) => (
        <div className="flex items-center gap-2">
          <Server className="size-4 d flex-shrink-0" />
          <span className="font-medium">{row.name}</span>
        </div>
      ),
    },
    {
      key: "type",
      header: "Type",
      align: "center",
      render: (row) => (
        <span
          className={`inline-block whitespace-nowrap px-2 py-0.5 rounded-md text-xs font-medium ${DEVICE_TYPE_COLORS[row.type] || ""}`}
        >
          {formatDeviceType(row.type)}
        </span>
      ),
    },
    {
      key: "ipAddress",
      header: "IP Address",
      align: "left",
      render: (row) => (
        <span className="font-mono text-sm font-medium text-foreground">
          {row.ipAddress ?? row.managementIp ?? row.virtualIp ?? "—"}
        </span>
      ),
    },
    {
      key: "os",
      header: "OS",
      align: "left",
      render: (row) => (
        <span className="text-foreground font-medium text-sm">
          {row.osName ? `${row.osName} ${row.osVersion || ""}`.trim() : "—"}
        </span>
      ),
    },
    {
      key: "status",
      header: "Status",
      align: "center",
      render: (row) => <StatusBadge status={row.status} />,
    },
  ];

  return (
    <div className="space-y-4">
      {/* ── Title row ── */}
      <div>
        <h1 className="text-2xl font-bold text-foreground">Devices</h1>
        <p className="text-sm d mt-1">
          Manage servers, VMs, firewalls, switches, routers, load balancers,
          containers and storage
        </p>
      </div>

      {/* ── Search bar + action buttons ── */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 d" />
          <input
            type="text"
            placeholder="Search by name, IP, hostname"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-slate-300 dark:border-white/20 rounded-lg bg-background focus:border-indigo-500 dark:focus:border-indigo-500 outline-none"
          />
        </div>
        <div className="flex items-center gap-2 ml-auto">
          {drawerDevice && (
            <div className="flex items-center gap-1 rounded-lg border border-slate-300 dark:border-white/20 bg-muted/40 p-1 mr-1">
              <button
                onClick={() => setDrawerActiveTab("info")}
                className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${
                  drawerActiveTab === "info"
                    ? "bg-indigo-600 text-white shadow-sm"
                    : "d hover:text-foreground hover:bg-muted/60"
                }`}
              >
                Info & Relationships
              </button>
              <Tip
                label={
                  !drawerHasRelationships
                    ? "Add a relationship to this device to view its topology"
                    : undefined
                }
                side="bottom"
              >
                <button
                  onClick={() =>
                    drawerHasRelationships && setDrawerActiveTab("topology")
                  }
                  disabled={!drawerHasRelationships}
                  className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${
                    drawerActiveTab === "topology"
                      ? "bg-indigo-600 text-white shadow-sm"
                      : "d hover:text-foreground hover:bg-muted/60"
                  } ${
                    !drawerHasRelationships
                      ? "opacity-40 cursor-not-allowed hover:bg-transparent hover:text-current"
                      : ""
                  }`}
                >
                  Topology
                </button>
              </Tip>
            </div>
          )}
          <button
            onClick={handleDownloadTemplate}
            disabled={templateDownloading}
            className="flex items-center gap-2 px-4 py-2 rounded-lg border border-border bg-muted/60 text-foreground text-sm font-medium hover:bg-indigo-50 dark:hover:bg-indigo-500/10 hover:border-indigo-400 dark:hover:border-indigo-400 hover:text-indigo-600 dark:hover:text-indigo-400 hover:-translate-y-px transition-all disabled:opacity-50 disabled:hover:translate-y-0"
          >
            <Upload className="size-4" />
            {templateDownloading ? "Exporting" : "Export Template"}
          </button>
          <RBACMultiAction
            checks={[
              ["cmdb", "create"],
              ["cmdb", "update"],
            ]}
            type="any"
          >
            <button
              onClick={openImportModal}
              className="flex items-center gap-2 px-3.5 py-2 rounded-xl border border-indigo-500 dark:border-indigo-400 text-sm font-bold text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-500/10 transition-all hover:-translate-y-px"
            >
              <Download className="size-4" />
              Import
            </button>
          </RBACMultiAction>
          <RBACMultiAction
            checks={[
              ["cmdb", "create"],
              ["cmdb", "update"],
            ]}
            type="any"
          >
            <button
              onClick={openCreate}
              className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors text-sm"
            >
              <Plus className="size-4" />
              Add Device
            </button>
          </RBACMultiAction>
        </div>
      </div>

      {/* ── Table (left) + inline detail panel (right) ── */}
      <div
        className={`flex gap-4 ${
          drawerDevice && drawerActiveTab === "topology"
            ? "h-[calc(100vh-140px)]"
            : "items-stretch"
        }`}
      >
        <div
          className={`flex-1 min-w-0 ${
            drawerDevice && drawerActiveTab === "topology" ? "hidden" : ""
          }`}
        >
          <DataTable
            columns={columns}
            data={paginated}
            loading={loading}
            emptyText="No devices found"
            page={page}
            pageSize={pageSize}
            totalItems={filtered.length}
            onPageChange={setPage}
            onRowClick={(row) => {
              setDrawerDevice(row);
              setDrawerActiveTab("info");
              setDrawerHasRelationships(false);
            }}
            rowKey={(row) => row.id}
            rowTooltip={(row) =>
              `Click to view ${row.name?.trim() || "device"} details`
            }
            rowClassName={(row) =>
              `relative hover:z-10 hover:shadow-md hover:bg-indigo-50/70 dark:hover:bg-white/[0.07] ${
                drawerDevice?.id === row.id
                  ? "!bg-indigo-500/[0.08] shadow-[inset_3px_0_0_0_#6366f1]"
                  : ""
              }`
            }
          />

          {/* Create / Edit Modal */}
          <Modal
            open={showModal}
            onClose={() => {
              if (!saving) setShowModal(false);
            }}
            title={editing ? "Edit Device" : "Add Device"}
            size="lg"
            footer={
              <ModalFooterButtons
                onCancel={() => setShowModal(false)}
                onSubmit={handleSubmit}
                submitLabel={saving ? "Saving…" : "Save"}
                submitDisabled={saving || !isFormValid}
              />
            }
          >
            <div className="space-y-3">
              {/* ── Section: Basic Information ── */}
              <div className="space-y-2.5">
                <p className="text-[10px] font-semibold tracking-widest d">
                  Basic Information
                </p>
                {/* Required fields first: Org Unit + Type */}
                <div className="grid grid-cols-2 gap-3">
                  <FormField label="Owning Org Unit" required>
                    {editing ? (
                      <p className="text-sm d px-3 py-2 border border-border rounded-lg bg-muted/50">
                        {orgOptions.find((o) => o.id === form.orgId)?.name ||
                          form.orgId}
                      </p>
                    ) : (
                      <FormSelect
                        value={form.orgId}
                        onChange={(v) => setForm({ ...form, orgId: v })}
                        options={[
                          { value: "", label: "Select organization" },
                          ...orgOptions.map((o) => ({
                            value: o.id,
                            label: o.name,
                          })),
                        ]}
                      />
                    )}
                  </FormField>
                  <FormField
                    label="Type"
                    required
                    hint={editing ? "Cannot change after creation" : undefined}
                  >
                    {editing ? (
                      <p className="text-sm d px-3 py-2 border border-border rounded-lg bg-muted/50">
                        {formatDeviceType(form.type)}
                      </p>
                    ) : (
                      <FormSelect
                        value={form.type}
                        onChange={(v) =>
                          setForm({
                            ...defaultForm,
                            orgId: form.orgId,
                            name: form.name,
                            status: form.status,
                            type: v as DeviceType,
                          })
                        }
                        options={[
                          { value: "", label: "Select type" },
                          { value: "SERVER", label: "Server" },
                          { value: "VM", label: "Virtual Machine" },
                          { value: "FIREWALL", label: "Firewall" },
                          { value: "SWITCH", label: "Switch" },
                          { value: "ROUTER", label: "Router" },
                          { value: "LB", label: "Load Balancer" },
                          { value: "CONTAINER", label: "Container" },
                          { value: "STORAGE", label: "Storage" },
                        ]}
                      />
                    )}
                  </FormField>
                </div>
                {/* Required: Name + Status */}
                <div className="grid grid-cols-2 gap-3">
                  <FormField label="Name" required>
                    <FormInput
                      value={form.name}
                      onChange={(v) => setForm({ ...form, name: v })}
                      placeholder="e.g. prod-db-01"
                    />
                  </FormField>
                  <FormField label="Status" required>
                    <FormSelect
                      value={form.status}
                      onChange={(v) =>
                        setForm({ ...form, status: v as CiStatus })
                      }
                      options={[
                        { value: "", label: "Select status" },
                        { value: "ACTIVE", label: "Active" },
                        { value: "INACTIVE", label: "Inactive" },
                        { value: "MAINTENANCE", label: "Maintenance" },
                        { value: "DECOMMISSIONED", label: "Decommissioned" },
                      ]}
                    />
                  </FormField>
                </div>
              </div>

              {/* ── Section: Connectivity ── */}
              {(f.ipAddress || f.managementIp || f.virtualIp || f.hostname) && (
                <div className="space-y-2.5 border-t border-border pt-3">
                  <p className="text-[10px] font-semibold tracking-widest d">
                    Connectivity
                  </p>
                  <div className="grid grid-cols-2 gap-3">
                    {/* Required first */}
                    {f.ipAddress && (
                      <FormField label="IP Address" required>
                        <FormInput
                          value={form.ipAddress}
                          onChange={(v) => setForm({ ...form, ipAddress: v })}
                          placeholder="e.g. 192.168.1.10"
                        />
                      </FormField>
                    )}
                    {f.hostname && (
                      <FormField label="Hostname" required>
                        <FormInput
                          value={form.hostname}
                          onChange={(v) => setForm({ ...form, hostname: v })}
                          placeholder="e.g. srv01.corp.local"
                        />
                      </FormField>
                    )}
                    {f.managementIp && (
                      <FormField label="Management IP" required>
                        <FormInput
                          value={form.managementIp}
                          onChange={(v) =>
                            setForm({ ...form, managementIp: v })
                          }
                          placeholder="e.g. 10.0.0.1"
                        />
                      </FormField>
                    )}
                    {f.virtualIp && (
                      <FormField label="Virtual IP" required>
                        <FormInput
                          value={form.virtualIp}
                          onChange={(v) => setForm({ ...form, virtualIp: v })}
                          placeholder="e.g. 10.0.0.100"
                        />
                      </FormField>
                    )}
                  </div>
                </div>
              )}

              {/* ── Section: Operating System ── */}
              {f.osName && (
                <div className="space-y-2.5 border-t border-border pt-3">
                  <p className="text-[10px] font-semibold tracking-widest d">
                    Operating System
                  </p>
                  <div className="grid grid-cols-2 gap-3">
                    <FormField label="OS Name" required>
                      <FormInput
                        value={form.osName}
                        onChange={(v) => setForm({ ...form, osName: v })}
                        placeholder="e.g. Ubuntu"
                      />
                    </FormField>
                    <FormField label="OS Version" required>
                      <FormInput
                        value={form.osVersion}
                        onChange={(v) => setForm({ ...form, osVersion: v })}
                        placeholder="e.g. 22.04"
                      />
                    </FormField>
                  </div>
                </div>
              )}

              {/* ── REQUIRED type-specific sections first ── */}

              {/* ── Section: Virtualisation (VM) — required, comes before optional hardware ── */}
              {f.hypervisor && (
                <div className="space-y-2.5 border-t border-border pt-3">
                  <p className="text-[10px] font-semibold tracking-widest d">
                    Virtualisation
                  </p>
                  <div className="grid grid-cols-2 gap-3">
                    <FormField label="Hypervisor" required>
                      <FormSelect
                        value={form.hypervisor}
                        onChange={(v) => setForm({ ...form, hypervisor: v })}
                        options={[
                          { value: "", label: "Select hypervisor" },
                          ...HYPERVISOR_OPTIONS,
                        ]}
                      />
                    </FormField>
                    <FormField label="Parent Server" required>
                      <FormSelect
                        value={form.parentCiId}
                        onChange={(v) => setForm({ ...form, parentCiId: v })}
                        options={[
                          { value: "", label: "Select server" },
                          ...serverOptions,
                        ]}
                      />
                    </FormField>
                  </div>
                </div>
              )}

              {/* ── Section: Container — required fields first ── */}
              {f.imageName && (
                <div className="space-y-2.5 border-t border-border pt-3">
                  <p className="text-[10px] font-semibold tracking-widest d">
                    Container
                  </p>
                  {/* Required */}
                  <div className="grid grid-cols-2 gap-3">
                    <FormField label="Image Name" required>
                      <FormInput
                        value={form.imageName}
                        onChange={(v) => setForm({ ...form, imageName: v })}
                        placeholder="e.g. nginx:1.25"
                      />
                    </FormField>
                    <FormField label="Namespace" required>
                      <FormInput
                        value={form.namespace}
                        onChange={(v) => setForm({ ...form, namespace: v })}
                        placeholder="e.g. production"
                      />
                    </FormField>
                  </div>
                  {f.parentCiId && !f.hypervisor && (
                    <div className="grid grid-cols-2 gap-3">
                      <FormField label="Host Server" required>
                        <FormSelect
                          value={form.parentCiId}
                          onChange={(v) => setForm({ ...form, parentCiId: v })}
                          options={[
                            { value: "", label: "Select server" },
                            ...serverOptions,
                          ]}
                        />
                      </FormField>
                      {/* Optional: Registry URL alongside required Host Server */}
                      <FormField label="Registry URL">
                        <FormInput
                          value={form.registryUrl}
                          onChange={(v) => setForm({ ...form, registryUrl: v })}
                          placeholder="e.g. registry.corp.local"
                        />
                      </FormField>
                    </div>
                  )}
                  {/* Optional: Registry URL alone if no Host Server */}
                  {!(f.parentCiId && !f.hypervisor) && (
                    <div className="grid grid-cols-2 gap-3">
                      <FormField label="Registry URL">
                        <FormInput
                          value={form.registryUrl}
                          onChange={(v) => setForm({ ...form, registryUrl: v })}
                          placeholder="e.g. registry.corp.local"
                        />
                      </FormField>
                      <div />
                    </div>
                  )}
                </div>
              )}

              {/* ── Section: Storage — required capacity first ── */}
              {f.totalCapacityTb && (
                <div className="space-y-2.5 border-t border-border pt-3">
                  <p className="text-[10px] font-semibold tracking-widest d">
                    Storage
                  </p>
                  <div className="grid grid-cols-2 gap-3">
                    <FormField label="Total Capacity (TB)" required>
                      <FormInput
                        type="number"
                        value={form.totalCapacityTb}
                        onChange={(v) =>
                          setForm({ ...form, totalCapacityTb: v })
                        }
                        placeholder="e.g. 10"
                      />
                    </FormField>
                    <FormField label="Used Capacity (TB)">
                      <FormInput
                        type="number"
                        value={form.usedCapacityTb}
                        onChange={(v) =>
                          setForm({ ...form, usedCapacityTb: v })
                        }
                        placeholder="e.g. 4.5"
                      />
                    </FormField>
                  </div>
                </div>
              )}

              {/* ── OPTIONAL sections last ── */}

              {/* ── Section: Hardware Specs (all optional) ── */}
              {f.cpuCores && (
                <div className="space-y-2.5 border-t border-border pt-3">
                  <p className="text-[10px] font-semibold tracking-widest d">
                    Hardware Specs{" "}
                    <span className="normal-case font-normal d/50">
                      (optional)
                    </span>
                  </p>
                  <div className="grid grid-cols-2 gap-3">
                    <FormField label="CPU Cores">
                      <FormInput
                        type="number"
                        value={form.cpuCores}
                        onChange={(v) => setForm({ ...form, cpuCores: v })}
                        placeholder="e.g. 8"
                      />
                    </FormField>
                    <FormField label="RAM (GB)">
                      <FormInput
                        type="number"
                        value={form.ramGb}
                        onChange={(v) => setForm({ ...form, ramGb: v })}
                        placeholder="e.g. 32"
                      />
                    </FormField>
                  </div>
                  {/* Disk paired with Description — both optional, saves a row */}
                  <div className="grid grid-cols-2 gap-3">
                    <FormField label="Disk (GB)">
                      <FormInput
                        type="number"
                        value={form.diskGb}
                        onChange={(v) => setForm({ ...form, diskGb: v })}
                        placeholder="e.g. 500"
                      />
                    </FormField>
                    <FormField label="Description">
                      <FormInput
                        value={form.description}
                        onChange={(v) => setForm({ ...form, description: v })}
                        placeholder="Optional"
                      />
                    </FormField>
                  </div>
                </div>
              )}

              {/* Description standalone — only for types without hardware (Firewall, Switch, Router etc.) */}
              {!f.cpuCores && (
                <div className="border-t border-border pt-3">
                  <FormField label="Description">
                    <FormInput
                      value={form.description}
                      onChange={(v) => setForm({ ...form, description: v })}
                      placeholder="Optional"
                    />
                  </FormField>
                </div>
              )}
            </div>
          </Modal>

          {/* Import Modal */}
          <Modal
            open={showImportModal}
            onClose={() => {
              if (!importLoading) {
                setShowImportModal(false);
              }
            }}
            title="Import Devices"
            size="md"
            footer={
              <ModalFooterButtons
                onCancel={() => setShowImportModal(false)}
                onSubmit={handleImportUpload}
                submitLabel={importLoading ? "Uploading…" : "Upload & Import"}
                submitDisabled={!importOrgId || !importFile || importLoading}
              />
            }
          >
            <div className="space-y-4">
              {!importResult && (
                <>
                  <p className="text-sm d">
                    Upload a CSV file to bulk-import devices. Download the
                    template to see the required columns (orgId is no longer a
                    CSV column — select the org unit below).
                  </p>
                  <FormField label="Department / Org Unit" required>
                    <FormSelect
                      value={importOrgId}
                      onChange={(v) => setImportOrgId(v)}
                      options={[
                        { value: "", label: "Select organization" },
                        ...orgOptions.map((o) => ({
                          value: o.id,
                          label: o.name,
                        })),
                      ]}
                    />
                  </FormField>
                  <div
                    className="border-2 border-dashed border-border rounded-lg p-6 text-center cursor-pointer hover:border-indigo-400 transition-colors"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <Download className="size-8 mx-auto mb-2 d" />
                    {importFile ? (
                      <div>
                        <p className="text-sm font-medium">{importFile.name}</p>
                        <p className="text-xs d mt-1">
                          {(importFile.size / 1024).toFixed(1)} KB
                        </p>
                        {importSizeWarned && (
                          <p className="text-xs text-amber-500 mt-1 flex items-center justify-center gap-1">
                            <AlertCircle className="size-3" />
                            File is over 5 MB — import may be slow
                          </p>
                        )}
                      </div>
                    ) : (
                      <div>
                        <p className="text-sm font-medium">
                          Click to select a CSV file
                        </p>
                        <p className="text-xs d mt-1">
                          Only .csv files accepted
                        </p>
                      </div>
                    )}
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".csv"
                    className="hidden"
                    onChange={(e) =>
                      handleFileChange(e.target.files?.[0] ?? null)
                    }
                  />
                </>
              )}

              {importResult && importResult.failed === 0 && (
                <div className="flex items-start gap-3 p-4 bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-200 dark:border-emerald-500/30 rounded-lg">
                  <CheckCircle2 className="size-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-emerald-800 dark:text-emerald-400">
                      Import Successful
                    </p>
                    <p className="text-sm text-emerald-700 dark:text-emerald-500 mt-0.5">
                      {importResult.success} device
                      {importResult.success !== 1 ? "s" : ""} imported
                      successfully.
                    </p>
                  </div>
                </div>
              )}

              {importResult &&
                importResult.failed > 0 &&
                importResult.success > 0 && (
                  <div className="space-y-3">
                    <div className="flex items-start gap-3 p-4 bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/30 rounded-lg">
                      <AlertCircle className="size-5 text-amber-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="font-medium text-amber-800 dark:text-amber-400">
                          Partial Import
                        </p>
                        <p className="text-sm text-amber-700 dark:text-amber-500 mt-0.5">
                          {importResult.success} imported successfully,{" "}
                          {importResult.failed} failed.
                        </p>
                      </div>
                    </div>
                    <ImportErrorTable
                      errors={importResult.errors}
                      onDownload={handleDownloadErrorReport}
                    />
                  </div>
                )}

              {importResult &&
                importResult.failed > 0 &&
                importResult.success === 0 && (
                  <div className="space-y-3">
                    <div className="flex items-start gap-3 p-4 bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/30 rounded-lg">
                      <AlertCircle className="size-5 text-red-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="font-medium text-red-800 dark:text-red-400">
                          Import Failed
                        </p>
                        <p className="text-sm text-red-700 dark:text-red-500 mt-0.5">
                          All {importResult.failed} rows had errors. No devices
                          were imported.
                        </p>
                      </div>
                    </div>
                    <ImportErrorTable
                      errors={importResult.errors}
                      onDownload={handleDownloadErrorReport}
                    />
                  </div>
                )}
            </div>
          </Modal>

          <DeleteConfirmDialog
            open={!!deletingCi}
            type="Device"
            name={deletingCi?.name || ""}
            deleting={deleting}
            onCancel={() => setDeletingCi(null)}
            onConfirm={confirmDelete}
          />
        </div>

        {/* ── Right: detail panel (inline, slides in from the right) ── */}
        {drawerDevice && (
          <div
            className={`flex-shrink-0 rounded-2xl border border-border bg-card shadow-lg overflow-hidden animate-in slide-in-from-right fade-in duration-300 ease-out ${
              drawerActiveTab === "topology"
                ? "w-full h-full"
                : "w-[550px] self-start h-[calc(100vh-280px)] relative"
            }`}
          >
            <div
              className={
                drawerActiveTab === "topology"
                  ? "h-full"
                  : "absolute inset-0 overflow-y-auto"
              }
            >
              <DeviceDrawer
                device={drawerDevice}
                onClose={() => {
                  setDrawerDevice(null);
                  setDrawerActiveTab("info");
                }}
                onEdit={(d) => openEdit(d)}
                onDelete={(d) => setDeletingCi(d)}
                activeTab={drawerActiveTab}
                setActiveTab={setDrawerActiveTab}
                onRelLoad={setDrawerHasRelationships}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
